import { STATUS_COLOR } from '@/features/orders/constants';

export type RtoTab = 'B2B' | 'B2C';

export interface RtoAddress {
  name?: string | null; company?: string | null; line1?: string | null; line2?: string | null;
  city?: string | null; state?: string | null; pincode?: string | null; country?: string | null; mobile?: string | null;
}

export interface RtoRow {
  id: string; orderNo: string; systemOrderNo?: string | null; awb?: string | null; awbNo?: string | null; lrNo?: string | null;
  orderType: RtoTab; orderDate?: string | null; lastEventDate?: string | null;
  statusId: number; statusName: string; isOpen: boolean;
  merchantId?: number; merchant?: string | null; courierId?: number | null; courier?: string | null;
  customerName?: string | null; customerMobile?: string | null;
  from: RtoAddress; to: RtoAddress; returnTo?: RtoAddress | null;
  rtoInitiatedAt?: string | null; rtoDeliveredAt?: string | null;
  rtoReceivedAt?: string | null; rtoReceivedBranch?: string | null; rtoReceivedBy?: string | null;
  merchantReturnDate?: string | null; merchantReturnBy?: string | null; hasProof?: boolean;
  /** Set once this returned shipment has gone out again under a new waybill. */
  redispatchedToOrderId?: string | null;
  /** The replacement shipment it went out as — order number and waybill. */
  redispatchedTo?: { id: string; orderNo: string; awb?: string | null; orderType: RtoTab } | null;
  courierReportedOnly?: boolean;
  rtoAgeingDays?: number | null; warehouseAgeingDays?: number | null;
  shipmentValue?: number; paymentType?: string;
}

export interface RtoMeta {
  statuses: Array<{ id: number; name: string; code: string }>;
  scanActions: Array<{
    action: 'RECEIVE' | 'HANDOVER' | 'RECEIVE_DAMAGED'; id: number; name: string; code: string; requiresProof: boolean;
    /** Remarks are mandatory (damaged receipt). */
    requiresRemarks?: boolean;
    /** 'media' = a photo or video, not the PDF / image POD a handover takes. */
    proofKind?: 'media';
  }>;
  /** The two statuses only the CS team sets, in bulk. */
  csRequestStatuses?: Array<{ id: number; name: string; code: string }>;
  branches: Array<{ id: number; name: string; code?: string | null; city?: string | null }>;
  defaultBranchId: number | null;
  ageingBuckets: Array<{ key: string; label: string }>;
  permissions: { viewB2B: boolean; viewB2C: boolean; exportB2B: boolean; exportB2C: boolean; receive: boolean; handover: boolean; override: boolean; csRequest?: boolean };
  isMerchant: boolean;
}

/** Statuses a parcel can be received from without an override (the backend decides; this only hides a button). */
export const RECEIVABLE_STATUS_IDS = [18, 10, 11, 12];
export const RECEIVED_STATUS_ID = 34;
export const HANDED_OVER_STATUS_ID = 35;
export const RECEIVED_DAMAGED_STATUS_ID = 36;
export const MERCHANT_DELIVERY_REQUESTED_STATUS_ID = 37;
export const REDISPATCH_REQUESTED_STATUS_ID = 38;
/** On our dock — received (clean or damaged) or waiting on a CS request. */
export const AT_WAREHOUSE_STATUS_IDS = [34, 36, 37, 38];

const IST = 5.5 * 3600 * 1000;
const p2 = (n: number) => String(n).padStart(2, '0');

/** DD-MM-YYYY in IST, whatever the viewer's own timezone. */
export function fmtDate(v?: string | null): string {
  if (!v) return '—';
  const d = new Date(new Date(v).getTime() + IST);
  if (isNaN(d.getTime())) return '—';
  return `${p2(d.getUTCDate())}-${p2(d.getUTCMonth() + 1)}-${d.getUTCFullYear()}`;
}

export function fmtDateTime(v?: string | null): string {
  if (!v) return '—';
  const d = new Date(new Date(v).getTime() + IST);
  if (isNaN(d.getTime())) return '—';
  return `${fmtDate(v)} ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`;
}

export function shortAddress(a?: RtoAddress | null): string {
  if (!a) return '—';
  return [a.city, a.state, a.pincode].filter(Boolean).join(', ') || a.line1 || '—';
}

export function fullAddress(a?: RtoAddress | null): string {
  if (!a) return '';
  return [a.name, a.company && a.company !== a.name ? a.company : null, a.line1, a.line2, a.city, a.state, a.pincode, a.country, a.mobile]
    .filter(Boolean).join(', ');
}

export function StatusChip({ id, name, hint }: { id: number; name: string; hint?: string }) {
  const tone = STATUS_COLOR[id] ?? 'bg-slate-50 text-slate-700 border-slate-200';
  return (
    <span title={hint} className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[11px] font-medium whitespace-nowrap ${tone}`}>
      {name}
    </span>
  );
}

/** Days, coloured by how long a return has been waiting: routine, overdue, escalate. */
export function AgeBadge({ days, open, label }: { days?: number | null; open: boolean; label?: string }) {
  if (days == null) return <span className="text-slate-400">—</span>;
  const tone = !open
    ? 'bg-slate-100 text-slate-600'
    : days > 15 ? 'bg-red-50 text-red-700' : days > 5 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${tone}`} title={open ? undefined : 'Return completed; time it took'}>
      {label ? `${label} ` : ''}{days}d
    </span>
  );
}

export function AddressBlock({ title, a, note }: { title: string; a?: RtoAddress | null; note?: string }) {
  const rows: Array<[string, string | null | undefined]> = [
    ['Name', a?.name], ['Company', a?.company], ['Address line 1', a?.line1], ['Address line 2', a?.line2],
    ['City', a?.city], ['State', a?.state], ['Pincode', a?.pincode], ['Country', a?.country], ['Mobile', a?.mobile],
  ];
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="text-xs font-semibold uppercase text-slate-500 mb-2">{title}</div>
      {!a ? <div className="text-xs text-slate-400">{note ?? 'Not recorded'}</div> : (
        <dl className="grid grid-cols-[110px_1fr] gap-x-2 gap-y-1 text-xs">
          {rows.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-slate-500">{k}</dt>
              <dd className="text-slate-800 break-words">{v || '—'}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
